<script setup>
import { computed, ref, watch } from 'vue';
import { useMobileDetection } from '@pfsc/core/ui-services/use-mobile-detection';
import { DEFAULT_QUERY_PARAMS } from '@pfsc/core/models/query-params';
import { queryParamsStorageService } from '@pfsc/core/ui-services/query-params-storage.service';
import { globalTranslate } from '../locale/app.i18n';

const props = defineProps({
  page: Number,
  perPage: Number,
  total: Number,
  totalPages: Number,
  disabled: {
    type: Boolean,
    default: false,
  },
});

const perPageOptions = [25, 50, 100];

const emits = defineEmits(['on-page-change']);
const { isMobile } = useMobileDetection();
const paginationConfig = ref({ page: props.page, perPage: props.perPage });

const currentResultRange = computed(() => {
  const start = (props.page - 1) * props.perPage + 1;
  const end = start + props.perPage - 1;
  const displayedEnd = props.total && end > props.total ? props.total : end;

  return {
    start,
    end: displayedEnd,
  };
});

/**
 * Handle pagination change.
 * @param {number} page Page.
 */
function onPaginationChange(page) {
  paginationConfig.value = { ...paginationConfig.value, page: Number(page) };
}

/**
 * Handle per page change.
 * @param {number} perPage Per page.
 */
function onPerPageChange(perPage) {
  paginationConfig.value = { page: DEFAULT_QUERY_PARAMS.page, perPage };
}

watch(() => props.page, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    paginationConfig.value.page = newValue;
  }
});

watch(() => props.perPage, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    paginationConfig.value.perPage = newValue;
  }
});

watch(() => paginationConfig.value, newValue => {
  queryParamsStorageService.setDefaultPerPage(newValue.perPage);
  emits('on-page-change', { perPage: newValue.perPage, page: newValue.page });
});

</script>
<template>
  <div :class="[$style.footerWrapper, 'pagination']">
    <div :class="$style.leftSide">
      <span :class="isMobile && $style.paginationInfo"><b>{{ globalTranslate('table.viewing') }} {{ currentResultRange.start }} -
        {{ currentResultRange.end }}</b>
        ({{ props.total }} {{ globalTranslate('table.total') }})</span>
      <v-select
        max-width="100px"
        hide-details
        density="compact"
        variant="outlined"
        :model-value="paginationConfig.perPage"
        :items="perPageOptions"
        :disabled="disabled"
        @update:model-value="onPerPageChange"
      />
    </div>
    <v-pagination
      :model-value="paginationConfig.page"
      :length="props.totalPages"
      :total-visible="7"
      size="small"
      :class="isMobile && $style.footerWrapperCenter"
      next-icon="fas fa-arrow-right"
      prev-icon="fas fa-arrow-left"
      :disabled="disabled"
      @update:model-value="onPaginationChange"
    />
  </div>
</template>

<style>

.pagination .v-pagination__list {
  flex-wrap: wrap;
  /* Override the default padding for pagination list. */
  margin-bottom: 0 !important;
  padding-left: 0 !important;
}

.pagination .v-pagination__item,
.pagination .v-pagination__prev,
.pagination .v-pagination__next{

  /* Override the default padding for pagination item. */
  padding-bottom: 0 !important;
}

.pagination .v-btn--icon.v-btn--density-default {
  /* Change the width to fit with ten thousand of pages. */
  width: calc(var(--v-btn-height) + 25px) !important;

  /* Override the default color for button. */
  color: var(--secondary-color) !important;
}
</style>

<style module>
.footerWrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.leftSide {
  display: flex;
  align-items: center;
  gap: 12px;
}

.footerWrapperCenter {
  margin: auto
}

.paginationInfo {
  margin: auto
}

@media screen and (max-width: 1104px) {
  .footerWrapperCenter {
    margin: 0;
  }
}
</style>
